import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { useI18n } from "../i18n";

const API_URL = process.env.REACT_APP_API_URL || '';

// Fix para iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Icono verde para destino
const greenIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Icono rojo para origen
const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Icono de preview para origen (rojo con borde discontinuo - más pequeño)
const previewOriginIcon = new L.DivIcon({
  className: 'preview-marker-icon',
  html: '<div style="background:#dc2626;border:3px dashed #991b1b;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 2px 8px rgba(220,38,38,0.5);opacity:0.85;">🔴</div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -12],
});

// Icono de preview para destino (verde con borde discontinuo - más pequeño)
const previewDestIcon = new L.DivIcon({
  className: 'preview-marker-icon',
  html: '<div style="background:#16a34a;border:3px dashed #065f46;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 2px 8px rgba(22,163,74,0.5);opacity:0.85;">🟢</div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -12],
});

// Componente para ajustar el zoom automáticamente
function FitBounds({ bounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, bounds]);
  
  return null;
}

// Componente para capturar la instancia del mapa Leaflet
function MapRefSetter({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    if (mapRef) mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

// Colores de icono de cargador según potencia máxima
// Operator letters for map markers
const OPERATOR_LETTERS = {
  'zunder': 'Z',
  'iberdrola': 'I',
  'repsol': 'R',
  'ionity': 'i',
  'acciona': 'A',
  'electromaps': 'e',
  'endesa': 'E',
  'tesla': 'T',
};
function getOperatorLetter(operator) {
  if (!operator) return null;
  const lower = operator.toLowerCase();
  for (const [key, letter] of Object.entries(OPERATOR_LETTERS)) {
    if (lower.includes(key)) return letter;
  }
  return null;
}

// Default favorite operator keywords
const DEFAULT_FAV_OPERATOR_KEYWORDS = ['repsol', 'zunder', 'tesla', 'iberdrola', 'ionity', 'acciona', 'endesa', 'electromaps', 'edp'];
const DEFAULT_FAVORITE_OPERATORS_DISPLAY = ['Acciona', 'EDP', 'Electromaps', 'Endesa', 'Iberdrola', 'Ionity', 'Repsol', 'Tesla', 'Zunder'];
function isDefaultFavOperator(operatorName) {
  const lower = (operatorName || '').toLowerCase();
  return DEFAULT_FAV_OPERATOR_KEYWORDS.some(kw => lower.includes(kw));
}

const chargerIconCache = {};
function getChargerIconByColor(bg, border, isTesla = false, operatorLetter = null) {
  const key = `${bg}-${border}-${isTesla ? 'T' : ''}-${operatorLetter || 'D'}`;
  if (!chargerIconCache[key]) {
    let innerContent;
    if (isTesla) {
      innerContent = `<svg viewBox="0 0 100 100" width="14" height="14"><path d="M50 5 L50 95 L45 95 L45 25 L30 25 C20 25 12 20 10 12 L10 5 L45 5 Z" fill="#e31937"/><path d="M50 5 L50 95 L55 95 L55 25 L70 25 C80 25 88 20 90 12 L90 5 L55 5 Z" fill="#e31937"/></svg>`;
    } else if (operatorLetter) {
      innerContent = `<span style="font-size:13px;font-weight:bold;color:#1e293b;line-height:1;font-family:Arial,sans-serif;">${operatorLetter}</span>`;
    } else {
      innerContent = `<span style="font-size:14px;">⚡</span>`;
    }
    chargerIconCache[key] = new L.DivIcon({
      className: 'charger-marker-icon',
      html: `<div style="background:${bg};border:2px solid ${border};border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${innerContent}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -14],
    });
  }
  return chargerIconCache[key];
}
function getChargerIcon(maxPower, isTesla = false, operatorLetter = null) {
  let bg, border;
  if (!maxPower || maxPower <= 22) { bg = '#86efac'; border = '#16a34a'; }
  else if (maxPower <= 50)         { bg = '#93c5fd'; border = '#2563eb'; }
  else if (maxPower <= 150)        { bg = '#fdba74'; border = '#c2410c'; }
  else                             { bg = '#c4b5fd'; border = '#7c3aed'; }
  return getChargerIconByColor(bg, border, isTesla, operatorLetter);
}
function getChargerIconForConfig(maxPower, powerRanges, isTesla = false, operatorLetter = null) {
  const power = maxPower || 0;
  for (const range of powerRanges) {
    if (range.type === 'exact' ? Math.abs(power - range.value) < 0.5 : power >= range.min && power <= range.max) {
      return getChargerIconByColor(range.color, range.borderColor, isTesla, operatorLetter);
    }
  }
  return getChargerIcon(maxPower, isTesla, operatorLetter);
}

// Rangos de potencia por defecto para la leyenda
export const DEFAULT_POWER_RANGES = [
  { min: 0, max: 22, type: 'range', color: '#86efac', borderColor: '#16a34a' },
  { min: 23, max: 50, type: 'range', color: '#93c5fd', borderColor: '#2563eb' },
  { min: 51, max: 150, type: 'range', color: '#fdba74', borderColor: '#c2410c' },
  { min: 151, max: 99999, type: 'range', color: '#c4b5fd', borderColor: '#7c3aed' },
];

function getRangeLabel(range) {
  if (range.type === 'exact') return `= ${range.value} kW`;
  if (range.min <= 0) return `≤${range.max} kW`;
  if (range.max >= 99999) return `>${range.min - 1} kW`;
  return `${range.min}–${range.max} kW`;
}

// Hook for draggable legend
function useDraggable(mapRef) {
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const didDragRef = useRef(false);

  useEffect(() => {
    if (dragging && mapRef?.current) {
      mapRef.current.dragging.disable();
      mapRef.current.touchZoom.disable();
    }
    if (!dragging) {
      if (mapRef?.current) {
        mapRef.current.dragging.enable();
        mapRef.current.touchZoom.enable();
      }
      // Reset didDragRef after a short delay so subsequent clicks on close work
      if (didDragRef.current) {
        const timer = setTimeout(() => { didDragRef.current = false; }, 200);
        return () => clearTimeout(timer);
      }
      return;
    }
    const handleMove = (e) => {
      if (e.cancelable) e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = Math.abs(cx - startRef.current.x);
      const dy = Math.abs(cy - startRef.current.y);
      if (dx > 3 || dy > 3) didDragRef.current = true;
      setPos({
        left: startRef.current.left + (cx - startRef.current.x),
        top: startRef.current.top + (cy - startRef.current.y),
      });
    };
    const handleUp = () => setDragging(false);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
  }, [dragging, mapRef]);

  const onDragStart = (e) => {
    // Don't start drag from close buttons or interactive elements
    if (e.target.closest('button, input, select, .legend-item, .info-tip-close')) return;
    const el = dragRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const parentRect = el.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    startRef.current = {
      x: cx,
      y: cy,
      left: pos ? pos.left : (rect.left - parentRect.left),
      top: pos ? pos.top : (rect.top - parentRect.top),
    };
    didDragRef.current = false;
    setDragging(true);
    e.preventDefault();
  };

  const dragStyle = pos ? {
    left: pos.left,
    top: pos.top,
    bottom: 'auto',
    right: 'auto',
    zIndex: dragging ? 1100 : undefined,
  } : {};

  return { dragRef, onDragStart, dragStyle, dragging, didDragRef };
}

// Icono para marcas fijadas en ruta (punto intermedio)
const pinIcon = new L.DivIcon({
  className: 'route-pin-icon',
  html: '<div style="background:#2563eb;border:2px solid #1d4ed8;border-radius:50%;width:14px;height:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -10],
});

// Icono para paradas de carga asignadas
const stopIcon = new L.DivIcon({
  className: 'route-stop-icon',
  html: '<div style="background:#dc2626;border:2px solid #991b1b;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.3);color:#fff;font-weight:bold;">⚡</div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -12],
});

// Icono para puntos de paso libres (via-points)
const viaIcon = new L.DivIcon({
  className: 'route-via-icon',
  html: '<div style="background:#f59e0b;border:3px solid #92400e;border-radius:50%;width:16px;height:16px;box-shadow:0 2px 6px rgba(0,0,0,0.5);"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -10],
});

// Icono para puntos de exploración (doble clic fuera de ruta)
const exploreIcon = new L.DivIcon({
  className: 'route-explore-icon',
  html: '<div style="background:#5eead4;border:2px solid #0d9488;border-radius:50%;width:14px;height:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -10],
});

// Componente para mostrar el nivel de zoom actual del mapa
function ZoomDisplay() {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => map.off('zoomend', onZoom);
  }, [map]);
  return (
    <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, background: 'rgba(255,255,255,0.92)', padding: '3px 8px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, color: '#334155', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', pointerEvents: 'none' }}>
      Zoom: {zoom}
    </div>
  );
}

// MarkerClusterGroup component using leaflet.markercluster
function ChargerClusterGroup({ children, chargers, visibleChargers, getIcon, onMarkerClick, showBubblePrice, showBubbleKwh, openPopupChargerIds, setOpenPopupChargerIds, openPopupChargerIdsRef, ranges, routePositions, cumDists, totalDistanceKm, allMarkedPoints, routeStops, favoriteChargers, onToggleFavorite, onAddRouteStop, groupConnectorsFn, clusterZoom }) {
  const map = useMap();
  const clusterRef = useRef(null);
  const markersRef = useRef({});
  const batchTimerRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    
    const disableZoom = Number(clusterZoom) || 10;
    const cluster = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: disableZoom,
      chunkedLoading: true,
      chunkInterval: 100,
      chunkDelay: 20,
      animate: true,
      iconCreateFunction: (clusterObj) => {
        const count = clusterObj.getChildCount();
        const markers = clusterObj.getAllChildMarkers();
        let size = 'small';
        let dim = 36;
        if (count >= 100) { size = 'large'; dim = 50; }
        else if (count >= 20) { size = 'medium'; dim = 42; }
        // Aggregate min price and max kW
        let minPrice = null;
        let maxKw = null;
        markers.forEach(m => {
          const data = m._chargerData;
          if (data) {
            if (data.maxPower && (maxKw === null || data.maxPower > maxKw)) maxKw = data.maxPower;
            if (data.priceNum != null && (minPrice === null || data.priceNum < minPrice)) minPrice = data.priceNum;
          }
        });
        const extraInfo = [];
        if (showBubblePrice && minPrice != null) extraInfo.push(`${minPrice.toFixed(2)}€`);
        if (showBubbleKwh && maxKw != null) extraInfo.push(`${maxKw}kW`);
        const extraHtml = extraInfo.length > 0 ? `<div class="cluster-extra-above">${extraInfo.join(' ')}</div>` : '';
        const extraWidth = extraInfo.length > 1 ? Math.max(dim, 70) : dim;
        return L.divIcon({
          html: `${extraHtml}<div class="cluster-icon cluster-icon-${size}"><span>${count}</span></div>`,
          className: 'charger-cluster-icon',
          iconSize: L.point(extraWidth, dim + (extraInfo.length > 0 ? 16 : 0)),
          iconAnchor: L.point(extraWidth / 2, dim + (extraInfo.length > 0 ? 16 : 0)),
        });
      },
    });

    clusterRef.current = cluster;
    map.addLayer(cluster);

    return () => {
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
      map.removeLayer(cluster);
      clusterRef.current = null;
      markersRef.current = {};
    };
  }, [map, clusterZoom, showBubblePrice, showBubbleKwh]);

  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster || !map) return;
    if (batchTimerRef.current) clearTimeout(batchTimerRef.current);

    // Clear existing markers
    cluster.clearLayers();
    markersRef.current = {};

    // Separate visible (in viewport) chargers from off-screen ones
    const bounds = map.getBounds();
    const inView = [];
    const offScreen = [];
    visibleChargers.forEach((charger, idx) => {
      const lat = charger.coordinates?.lat || charger.lat;
      const lng = charger.coordinates?.lng || charger.lng;
      if (!lat || !lng) return;
      if (bounds.contains([lat, lng])) {
        inView.push({ charger, idx });
      } else {
        offScreen.push({ charger, idx });
      }
    });

    // Helper to create a marker
    const createMarker = (charger, idx) => {
      const lat = charger.coordinates?.lat || charger.lat;
      const lng = charger.coordinates?.lng || charger.lng;
      // Stable id: use charger.id when present, otherwise a stable fallback
      // based on the index. This ensures the favorite comparison works even
      // when chargers don't carry an explicit `id` from the API.
      const id = charger.id != null ? charger.id : `auto-${idx}`;
      const isTesla = (charger.operator || '').toLowerCase().includes('tesla');
      const operatorLetter = getOperatorLetter(charger.operator);
      let icon = getIcon(charger.maxPower, ranges, isTesla, operatorLetter);

      // If bubble mode is active, create a custom icon with price/kWh label above
      const showBubble = (showBubblePrice || showBubbleKwh);
      if (showBubble) {
        const priceShort = (() => {
          const cost = charger.usageCost || '';
          const euroIdx = cost.indexOf('€');
          if (euroIdx >= 0) return cost.substring(0, euroIdx + 1);
          return cost || '';
        })();
        const bubbleText = [
          showBubblePrice && priceShort ? priceShort : null,
          showBubbleKwh && charger.maxPower ? `${charger.maxPower}kW` : null,
        ].filter(Boolean).join('/');
        if (bubbleText) {
          const baseIcon = icon;
          const baseHtml = baseIcon.options.html || '';
          icon = new L.DivIcon({
            className: 'charger-marker-icon',
            html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center"><div class="charger-bubble-label" style="background:rgba(255,255,255,0.92);border:1px solid #d1d5db;border-radius:6px;padding:1px 5px;font-size:0.65rem;font-weight:600;color:#374151;white-space:nowrap;margin-bottom:2px;box-shadow:0 1px 3px rgba(0,0,0,0.1)">${bubbleText}</div>${baseHtml}</div>`,
            iconSize: [24, 40],
            iconAnchor: [12, 40],
            popupAnchor: [0, -40],
          });
        }
      }

      const marker = L.marker([lat, lng], { icon, zIndexOffset: 500 });

      const priceNum = (() => {
        const cost = charger.usageCost || '';
        const match = cost.match(/([\d,.]+)/);
        return match ? parseFloat(match[1].replace(',', '.')) : null;
      })();
      marker._chargerData = { id, maxPower: charger.maxPower || 0, priceNum };

      const isFav = favoriteChargers.some(f => f.id === id);
      const popupContent = document.createElement('div');
      popupContent.className = 'charger-cluster-popup';
      popupContent.innerHTML = buildChargerPopupHTML(charger, routePositions, cumDists, totalDistanceKm, allMarkedPoints, groupConnectorsFn, isFav);
      
      // Add event listeners for popup buttons
      const attachPopupEvents = () => {
        const minimizeBtns = popupContent.querySelectorAll('.popup-minimize-btn');
        const maximizeBtns = popupContent.querySelectorAll('.popup-maximize-btn');
        const closeBtns = popupContent.querySelectorAll('.popup-close-btn');
        const favBtns = popupContent.querySelectorAll('.popup-fav-btn');
        const fullDiv = popupContent.querySelector('.charger-popup-full');
        const miniDiv = popupContent.querySelector('.charger-popup-mini');
        
        minimizeBtns.forEach(btn => btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (fullDiv) fullDiv.style.display = 'none';
          if (miniDiv) miniDiv.style.display = 'block';
        }));
        maximizeBtns.forEach(btn => btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (fullDiv) fullDiv.style.display = 'block';
          if (miniDiv) miniDiv.style.display = 'none';
        }));
        closeBtns.forEach(btn => btn.addEventListener('click', (e) => {
          e.stopPropagation();
          marker.closePopup();
          const cid = charger.id || idx;
          setOpenPopupChargerIds(prev => { const s = new Set(prev); s.delete(cid); return s; });
        }));
        favBtns.forEach(btn => btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (onToggleFavorite) onToggleFavorite({ ...charger, id });
          // Optimistic visual update: flip the star to reflect the new state immediately,
          // regardless of marker re-render timing. After the toggle, favoriteChargers
          // will contain this charger if it wasn't a fav, or will no longer if it was.
          const willBeFav = !favoriteChargers.some(f => f.id === id);
          const newColor = willBeFav ? '#f59e0b' : '#6b7280';
          const newBg = willBeFav ? '#fef3c7' : '#f3f4f6';
          const newBorder = willBeFav ? '#fbbf24' : '#d1d5db';
          const newChar = willBeFav ? '★' : '☆';
          favBtns.forEach(b => {
            b.style.color = newColor;
            b.style.background = newBg;
            b.style.borderColor = newBorder;
            b.textContent = newChar;
          });
        }));
      };
      attachPopupEvents();
      
      marker.bindPopup(popupContent, { maxWidth: 300, minWidth: 50, autoClose: false, closeOnClick: false, closeButton: false });
      marker.on('popupopen', () => {
        const cid = id;
        setOpenPopupChargerIds(prev => { const s = new Set(prev); s.add(cid); return s; });
        attachPopupEvents();
        // Hide bubble label when popup is open
        const bubbleEl = marker.getElement()?.querySelector('.charger-bubble-label');
        if (bubbleEl) bubbleEl.style.display = 'none';
      });
      marker.on('popupclose', () => {
        const cid = id;
        setOpenPopupChargerIds(prev => { const s = new Set(prev); s.delete(cid); return s; });
        // Restore bubble label when popup closes
        const bubbleEl = marker.getElement()?.querySelector('.charger-bubble-label');
        if (bubbleEl) bubbleEl.style.display = '';
      });

      const operator = charger.operator || 'Unknown';
      const power = charger.maxPower ? `⚡${charger.maxPower}kW` : '';
      const price = charger.usageCost || '€--/kWh';
      
      // Always bind a hover tooltip with full charger info
      const tooltipContent = `<div style="font-size:0.78em;line-height:1.3;max-width:200px"><strong>${operator}</strong><br/>${power} <span style="color:#059669;font-weight:bold">💰${price}</span></div>`;
      marker.bindTooltip(tooltipContent, { direction: 'top', offset: [0, -12], permanent: false, className: 'charger-map-tooltip' });
      return { marker, id: charger.id || idx };
    };

    // Add visible chargers first (immediate)
    inView.forEach(({ charger, idx }) => {
      const { marker, id } = createMarker(charger, idx);
      markersRef.current[id] = marker;
      cluster.addLayer(marker);
    });

    // Add off-screen chargers in batches progressively
    const BATCH_SIZE = 80;
    let offset = 0;
    const addBatch = () => {
      if (offset >= offScreen.length) return;
      const batch = offScreen.slice(offset, offset + BATCH_SIZE);
      const layersToAdd = [];
      batch.forEach(({ charger, idx }) => {
        const { marker, id } = createMarker(charger, idx);
        markersRef.current[id] = marker;
        layersToAdd.push(marker);
      });
      cluster.addLayers(layersToAdd);
      offset += BATCH_SIZE;
      if (offset < offScreen.length) {
        batchTimerRef.current = setTimeout(addBatch, 50);
      }
    };
    if (offScreen.length > 0) {
      batchTimerRef.current = setTimeout(addBatch, 30);
    }

    // Re-open pinned popups after markers are created
    const reopenPinnedPopups = () => {
      const pinned = openPopupChargerIdsRef.current;
      if (!pinned || pinned.size === 0) return;
      pinned.forEach(cid => {
        const m = markersRef.current[cid];
        if (m && map.hasLayer(m)) {
          m.openPopup();
          // Hide bubble label if popup is open
          const bubbleEl = m.getElement()?.querySelector('.charger-bubble-label');
          if (bubbleEl) bubbleEl.style.display = 'none';
        }
      });
    };
    // Initial re-open for in-view markers
    setTimeout(reopenPinnedPopups, 100);

    // Listen for cluster animation end to re-open popups when unclustering
    const onAnimationEnd = () => { setTimeout(reopenPinnedPopups, 50); };
    cluster.on('animationend', onAnimationEnd);

    return () => {
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
      cluster.off('animationend', onAnimationEnd);
    };
  }, [visibleChargers, ranges, showBubblePrice, showBubbleKwh, routePositions, cumDists, totalDistanceKm, allMarkedPoints, groupConnectorsFn]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the favorites change, update ONLY the star button of the currently
  // open popups (if any). We deliberately do NOT include favoriteChargers in
  // the marker-creation deps above because re-creating every marker would
  // close the popup the user just clicked on.
  useEffect(() => {
    const pinned = openPopupChargerIdsRef.current;
    if (!pinned || pinned.size === 0) return;
    pinned.forEach(cid => {
      const m = markersRef.current[cid];
      if (!m || !m.getPopup) return;
      const popupEl = m.getPopup() && m.getPopup().getElement && m.getPopup().getElement();
      if (!popupEl) return;
      const favBtns = popupEl.querySelectorAll('.popup-fav-btn');
      if (!favBtns || favBtns.length === 0) return;
      const chargerData = m._chargerData || {};
      // Use the stable id from the marker's charger data when available
      const lookupId = chargerData.id != null ? chargerData.id : cid;
      const isFav = favoriteChargers.some(f => f.id === lookupId);
      const starColor = isFav ? '#f59e0b' : '#6b7280';
      const starBg = isFav ? '#fef3c7' : '#f3f4f6';
      const starBorder = isFav ? '#fbbf24' : '#d1d5db';
      const starChar = isFav ? '★' : '☆';
      favBtns.forEach(b => {
        b.style.color = starColor;
        b.style.background = starBg;
        b.style.borderColor = starBorder;
        b.textContent = starChar;
        b.title = isFav ? 'Quitar favorito' : 'Marcar favorito';
      });
    });
  }, [favoriteChargers]);

  return null;
}

// Build HTML content for charger popup in cluster mode
function buildChargerPopupHTML(charger, routePositions, cumDists, totalDistanceKm, allMarkedPoints, groupConnectorsFn, isFav) {
  const operator = charger.operator || 'Desconocido';
  const grouped = groupConnectorsFn(charger.connectors);
  const address = charger.address
    ? (typeof charger.address === 'string'
        ? charger.address
        : [charger.address?.street, charger.address?.city].filter(Boolean).join(', '))
    : '';

  let deviationHtml = '';
  if (routePositions && routePositions.length > 1) {
    const lat = charger.coordinates?.lat || charger.lat;
    const lng = charger.coordinates?.lng || charger.lng;
    if (lat && lng) {
      const chargerLatLng = L.latLng(lat, lng);
      const deviationKm = getDeviationFromRoute(chargerLatLng, routePositions);
      deviationHtml = `<div style="margin-top:2px;padding:2px 5px;background:#f0f4ff;border-radius:3px;font-size:0.78em"><span style="color:#dc2626">↗${deviationKm.toFixed(1)}km</span></div>`;
    }
  }

  const connectorsHtml = grouped.map(g =>
    `<span style="display:inline-block;padding:1px 4px;margin:1px;background:${g.power > 150 ? '#ede9fe' : g.power > 50 ? '#fff7ed' : '#f0fdf4'};border-radius:3px;font-size:0.82em">${g.type}${g.power > 0 ? ` ${g.power}kW` : ''} (${g.count})</span>`
  ).join('');

  const starColor = isFav ? '#f59e0b' : '#6b7280';
  const starBg = isFav ? '#fef3c7' : '#f3f4f6';
  const starBorder = isFav ? '#fbbf24' : '#d1d5db';
  const starChar = isFav ? '★' : '☆';

  return `
    <div class="charger-popup-full" style="font-size:0.82em;line-height:1.35;min-width:180px;max-width:240px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px">
        <strong style="font-size:0.95em;flex:1;margin-right:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${operator}</strong>
        <div style="display:flex;gap:2px;flex-shrink:0">
          <button class="popup-fav-btn" style="background:${starBg};border:1px solid ${starBorder};border-radius:3px;cursor:pointer;font-size:11px;padding:1px 4px;line-height:1;min-width:22px;min-height:20px;color:${starColor};display:inline-flex;align-items:center;justify-content:center" title="Favorito">${starChar}</button>
          <button class="popup-minimize-btn" style="background:#f3f4f6;border:1px solid #d1d5db;border-radius:3px;cursor:pointer;font-size:12px;padding:1px 4px;line-height:1;min-width:22px;min-height:20px;color:#374151;display:inline-flex;align-items:center;justify-content:center" title="Minimizar">—</button>
          <button class="popup-close-btn" style="background:#f3f4f6;border:1px solid #d1d5db;border-radius:3px;cursor:pointer;font-size:12px;padding:1px 4px;line-height:1;min-width:22px;min-height:20px;color:#dc2626;display:inline-flex;align-items:center;justify-content:center" title="Cerrar">✕</button>
        </div>
      </div>
      <div style="margin:2px 0;padding:2px 0;border-top:1px solid #e5e7eb;border-bottom:1px solid #e5e7eb">${connectorsHtml}</div>
      ${charger.maxPower ? `<div style="font-size:0.9em"><span style="color:#2563eb;font-weight:bold">⚡${charger.maxPower}kW</span></div>` : ''}
      <div style="font-size:0.9em"><span style="color:#059669;font-weight:bold">💰 ${charger.usageCost || '€--/kWh'}</span></div>
      ${address ? `<div style="color:#666;font-size:0.8em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${address}</div>` : ''}
      ${deviationHtml}
    </div>
    <div class="charger-popup-mini" style="display:none">
      <div style="display:flex;align-items:center;gap:2px;margin-bottom:1px">
        <strong style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:0.85em">${operator}</strong>
        <button class="popup-fav-btn" style="background:${starBg};border:1px solid ${starBorder};border-radius:3px;cursor:pointer;font-size:9px;padding:0px 2px;line-height:1;min-width:14px;min-height:14px;color:${starColor};display:inline-flex;align-items:center;justify-content:center" title="Favorito">${starChar}</button>
        <button class="popup-maximize-btn" style="background:#f3f4f6;border:1px solid #d1d5db;border-radius:3px;cursor:pointer;font-size:9px;padding:0px 2px;line-height:1;min-width:14px;min-height:14px;color:#374151;display:inline-flex;align-items:center;justify-content:center" title="Expandir">□</button>
        <button class="popup-close-btn" style="background:#f3f4f6;border:1px solid #d1d5db;border-radius:3px;cursor:pointer;font-size:9px;padding:0px 2px;line-height:1;min-width:14px;min-height:14px;color:#dc2626;display:inline-flex;align-items:center;justify-content:center" title="Cerrar">✕</button>
      </div>
      <div style="display:flex;align-items:center;gap:2px">
        ${charger.maxPower ? `<span style="color:#2563eb;font-weight:bold;font-size:0.85em">⚡${Math.round(charger.maxPower)}kW</span>` : ''}
        <span style="color:#059669;font-weight:bold;font-size:0.85em">${charger.usageCost || ''}</span>
      </div>
    </div>
  `;
}

// Calcula distancia acumulada en km a lo largo de la polyline
function computeCumulativeDistances(positions) {
  const dists = [0];
  for (let i = 1; i < positions.length; i++) {
    const [lat1, lng1] = positions[i - 1];
    const [lat2, lng2] = positions[i];
    const d = L.latLng(lat1, lng1).distanceTo(L.latLng(lat2, lng2)) / 1000;
    dists.push(dists[i - 1] + d);
  }
  return dists;
}

function projectPointOnSegment(p, a, b) {
  const x = p.lng, y = p.lat;
  const x1 = a.lng, y1 = a.lat;
  const x2 = b.lng, y2 = b.lat;
  const dx = x2 - x1, dy = y2 - y1;
  if (dx === 0 && dy === 0) return a;
  let t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));
  return L.latLng(y1 + t * dy, x1 + t * dx);
}

function getDistanceAlongRoute(latlng, positions, cumDists) {
  let minDist = Infinity;
  let bestKm = 0;
  for (let i = 0; i < positions.length - 1; i++) {
    const a = L.latLng(positions[i]);
    const b = L.latLng(positions[i + 1]);
    const proj = projectPointOnSegment(latlng, a, b);
    const d = latlng.distanceTo(proj);
    if (d < minDist) {
      minDist = d;
      const segLen = a.distanceTo(b);
      const projLen = a.distanceTo(proj);
      const ratio = segLen > 0 ? projLen / segLen : 0;
      bestKm = cumDists[i] + ratio * (cumDists[i + 1] - cumDists[i]);
    }
  }
  return bestKm;
}

// Distancia perpendicular de un punto a la ruta (desviación)
function getDeviationFromRoute(latlng, positions) {
  let minDist = Infinity;
  for (let i = 0; i < positions.length - 1; i++) {
    const a = L.latLng(positions[i]);
    const b = L.latLng(positions[i + 1]);
    const proj = projectPointOnSegment(latlng, a, b);
    const d = latlng.distanceTo(proj) / 1000; // km
    if (d < minDist) minDist = d;
  }
  return minDist;
}

// Agrupa conectores por tipo+potencia y devuelve formato "CCS2 120kW (2)"
function groupConnectors(connectors) {
  if (!connectors || !Array.isArray(connectors)) return [];
  const groups = {};
  for (const c of connectors) {
    const type = (c.type || 'Unknown').toUpperCase();
    const power = c.power ? Math.round(c.power) : 0;
    const key = `${type}-${power}`;
    if (!groups[key]) {
      groups[key] = { type, power, count: 0 };
    }
    groups[key].count += (c.quantity || 1);
  }
  return Object.values(groups).sort((a, b) => b.power - a.power);
}

// Encuentra el punto anterior y siguiente en los puntos fijados (sorted) dado un km
function findPrevNextKm(km, sortedPins, totalKm) {
  let prevKm = 0;
  let nextKm = totalKm;
  for (const p of sortedPins) {
    if (p.km < km) prevKm = p.km;
    else { nextKm = p.km; break; }
  }
  return { prevKm, nextKm };
}

// Maneja click en el mapa cuando el modo via-point está activo
function MapClickHandler({ viaPointMode, onAddViaPoint, mapMode, onSetOrigin, onSetDestination, routePositions, explorePins, onAddExplorePin, onRemoveExplorePin, chargerQueryMode }) {
  const map = useMap();
  useMapEvents({
    click(e) {
      if (mapMode === 'setOrigin' && onSetOrigin) {
        onSetOrigin({ lat: e.latlng.lat, lng: e.latlng.lng });
        return;
      }
      if (mapMode === 'setDestination' && onSetDestination) {
        onSetDestination({ lat: e.latlng.lat, lng: e.latlng.lng });
        return;
      }
      if (!viaPointMode) return;
      onAddViaPoint({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
    dblclick(e) {
      if (chargerQueryMode) return;
      if (viaPointMode || mapMode) return;
      // Skip if near route (RouteDistanceTooltip handles it)
      if (routePositions && routePositions.length > 1) {
        const point = map.latLngToContainerPoint(e.latlng);
        for (let i = 0; i < routePositions.length - 1; i++) {
          const a = map.latLngToContainerPoint(L.latLng(routePositions[i]));
          const b = map.latLngToContainerPoint(L.latLng(routePositions[i + 1]));
          if (L.LineUtil.pointToSegmentDistance(point, a, b) < 30) return;
        }
      }
      // Check if near existing exploration pin (remove it)
      if (explorePins && explorePins.length > 0) {
        const clickPoint = map.latLngToContainerPoint(e.latlng);
        const idx = explorePins.findIndex(p => {
          const pinPoint = map.latLngToContainerPoint(L.latLng(p.lat, p.lng));
          return clickPoint.distanceTo(pinPoint) < 20;
        });
        if (idx >= 0) {
          onRemoveExplorePin(idx);
          return;
        }
      }
      // Add new exploration pin
      if (onAddExplorePin) {
        onAddExplorePin({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    },
  });
  return null;
}

// Tooltip que muestra info de ubicación al mover el ratón en modo setOrigin/setDestination
function MapModeTooltip({ mapMode }) {
  const map = useMap();
  const tooltipRef = useRef(null);
  const debounceRef = useRef(null);
  const cacheRef = useRef({});

  useEffect(() => {
    if (!map || !mapMode) {
      if (tooltipRef.current && map?.hasLayer(tooltipRef.current)) {
        map.removeLayer(tooltipRef.current);
      }
      return;
    }

    const tooltip = L.tooltip({
      permanent: false,
      direction: 'top',
      offset: [0, -15],
      className: 'map-location-tooltip',
    });
    tooltipRef.current = tooltip;

    const handleMouseMove = (e) => {
      const { lat, lng } = e.latlng;
      const label = mapMode === 'setOrigin' ? '📍 Origen' : '📍 Destino';
      const cacheKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;
      if (cacheRef.current[cacheKey]) {
        tooltip.setContent(`<div style="text-align:center"><strong>${label}</strong><br/>${cacheRef.current[cacheKey]}<br/><span style="font-size:0.8em;color:#888">${lat.toFixed(5)}, ${lng.toFixed(5)}</span></div>`);
      } else {
        tooltip.setContent(`<div style="text-align:center"><strong>${label}</strong><br/>${lat.toFixed(5)}, ${lng.toFixed(5)}</div>`);
      }
      tooltip.setLatLng(e.latlng);
      if (!map.hasLayer(tooltip)) tooltip.addTo(map);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch(`${API_URL}/api/geocode/reverse?lat=${lat}&lng=${lng}`);
          const data = await res.json();
          if (data.success && data.data) {
            const addr = data.data.address || {};
            const road = addr.road || addr.pedestrian || addr.footway || '';
            const houseNumber = addr.house_number || '';
            const city = addr.city || addr.town || addr.village || addr.municipality || '';
            const parts = [];
            if (road) parts.push(houseNumber ? `${road} ${houseNumber}` : road);
            if (city && city !== road) parts.push(city);
            const name = parts.length > 0 ? parts.join(', ') : (data.data.display_name?.split(',').slice(0, 2).join(',').trim() || '');
            if (name) {
              cacheRef.current[cacheKey] = name;
              if (map.hasLayer(tooltip)) {
                tooltip.setContent(`<div style="text-align:center"><strong>${label}</strong><br/>${name}<br/><span style="font-size:0.8em;color:#888">${lat.toFixed(5)}, ${lng.toFixed(5)}</span></div>`);
              }
            }
          }
        } catch {}
      }, 400);
    };

    const handleMouseOut = () => {
      if (map.hasLayer(tooltip)) map.removeLayer(tooltip);
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseOut);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseOut);
      if (map.hasLayer(tooltip)) map.removeLayer(tooltip);
    };
  }, [map, mapMode]);

  return null;
}

// Leyenda de potencias de cargadores (overlay exterior al MapContainer) — interactiva
function PowerLegend({ t, powerRanges, legendFilters, onToggleLegendFilter, powerRangeConfig, updatePowerRange, savePowerRangeConfig, resetPowerRangeConfig, configSaved, currentUser, mapRef }) {
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const { dragRef, onDragStart, dragStyle, dragging, didDragRef } = useDraggable(mapRef);
  return (
    <div ref={dragRef} className="map-power-legend interactive" style={{ ...dragStyle, cursor: dragging ? 'grabbing' : undefined }}>
      <div className="legend-header-row" onMouseDown={onDragStart} onTouchStart={onDragStart} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'grab', userSelect: 'none' }}>
        <span style={{ color: '#9ca3af', fontSize: '0.75em', cursor: 'grab' }}>⋮⋮</span>
        <div className="legend-title" onClick={() => { if (!didDragRef.current) setMinimized(v => !v); }} style={{ cursor: 'pointer', flex: 1, marginBottom: 0 }}>
          ⚡ {t('chargers.legend')} {minimized ? '▸' : '▾'}
        </div>
      </div>
      {!minimized && (<>
      <div onClick={() => setShowConfigPopup(v => !v)} style={{ cursor: 'pointer', textAlign: 'center', fontSize: '0.72em', color: '#2563eb', marginBottom: 4, userSelect: 'none' }} title={t('legend.clickToConfigure')}>
        {t('legend.clickToConfigure')}
      </div>
      {powerRanges.map((range, idx) => {
        const active = legendFilters.has(idx);
        return (
          <div
            key={idx}
            className={`legend-item clickable${active ? '' : ' inactive'}`}
            onClick={() => onToggleLegendFilter(idx)}
            title={active ? t('legend.clickToHide') : t('legend.clickToShow')}
          >
            <span className="legend-dot" style={{ background: range.color, border: `2px solid ${range.borderColor}`, opacity: active ? 1 : 0.2 }} />
            <span style={{ color: active ? 'inherit' : '#6b7280' }}>{getRangeLabel(range)}</span>
          </div>
        );
      })}
      {showConfigPopup && (
        <div className="legend-config-popup">
          <div className="legend-config-popup-header">
            <h3>⚡ {t('tab.powerRangesTitle')}</h3>
            <button className="legend-config-popup-close" onClick={() => setShowConfigPopup(false)}>✕</button>
          </div>
          <p style={{ fontSize: '0.85em', color: '#666', marginBottom: 12 }}>{t('tab.powerRangesDesc')}</p>
          {powerRangeConfig.map((range, idx) => {
            const colorEmojis = ['🟢', '🔵', '🟠', '🟣'];
            const colorNames = [t('legend.green'), t('legend.blue'), t('legend.orange'), t('legend.purple')];
            return (
              <div key={idx} className="power-range-config-row">
                <span className="range-color-label" title={colorNames[idx]}>{colorEmojis[idx]}</span>
                <span className="range-color-dot" style={{ background: range.color, border: `2px solid ${range.borderColor}` }} />
                <select
                  value={range.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    if (newType === 'exact') {
                      updatePowerRange(idx, { type: 'exact', value: range.min || 50, color: range.color, borderColor: range.borderColor });
                    } else {
                      updatePowerRange(idx, { type: 'range', min: range.value || 0, max: range.value ? range.value * 2 : 100, color: range.color, borderColor: range.borderColor });
                    }
                  }}
                  className="range-type-select"
                >
                  <option value="range">{t('legend.interval')}</option>
                  <option value="exact">{t('legend.exact')}</option>
                </select>
                {range.type === 'range' ? (
                  <>
                    <label className="range-input-label">
                      {t('legend.min')}:
                      <input
                        type="number"
                        value={range.min}
                        onChange={(e) => updatePowerRange(idx, { ...range, min: Number(e.target.value) || 0 })}
                        className="range-input"
                        min="0"
                      />
                    </label>
                    <label className="range-input-label">
                      {t('legend.max')}:
                      <input
                        type="number"
                        value={range.max >= 99999 ? '' : range.max}
                        onChange={(e) => updatePowerRange(idx, { ...range, max: e.target.value === '' ? 99999 : Number(e.target.value) || 0 })}
                        className="range-input"
                        min="0"
                        placeholder="∞"
                      />
                    </label>
                    <span className="range-unit">kW</span>
                  </>
                ) : (
                  <>
                    <label className="range-input-label">
                      {t('legend.value')}:
                      <input
                        type="number"
                        value={range.value || ''}
                        onChange={(e) => updatePowerRange(idx, { ...range, value: Number(e.target.value) || 0 })}
                        className="range-input"
                        min="0"
                      />
                    </label>
                    <span className="range-unit">kW</span>
                  </>
                )}
              </div>
            );
          })}
          <div className="power-config-actions">
            <button type="button" onClick={savePowerRangeConfig} className="calculate-btn" style={{ padding: '6px 16px', fontSize: '0.85em' }}>
              💾 {t('legend.save')}
            </button>
            <button type="button" onClick={resetPowerRangeConfig} className="clear-filters-btn" style={{ padding: '6px 12px', fontSize: '0.85em' }}>
              ↺ {t('legend.reset')}
            </button>
            {configSaved && <span style={{ color: '#059669', fontSize: '0.85em', fontWeight: 600 }}>✅ {t('legend.saved')}</span>}
            {currentUser && <span style={{ color: '#2563eb', fontSize: '0.8em' }}>👤 {t('legend.savedToProfile')}</span>}
            {!currentUser && <span style={{ color: '#92400e', fontSize: '0.8em' }}>⚠️ {t('legend.loginToSave')}</span>}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

// Connector type icons/colors
const CONNECTOR_STYLES = {
  'CCS (Type 2)': { emoji: '⚡', color: '#7c3aed' },
  'CHAdeMO': { emoji: '⚡', color: '#dc2626' },
  'Type 2 (Socket Only)': { emoji: '🔌', color: '#2563eb' },
  'Type 2 (Tethered Connector)': { emoji: '🔌', color: '#0891b2' },
  'Type 1 (J1772)': { emoji: '🔌', color: '#65a30d' },
  'Tesla (Model S/X)': { emoji: '⚡', color: '#e11d48' },
  'Schuko (CEE 7/4)': { emoji: '🔌', color: '#78716c' },
};
function getConnectorStyle(type) {
  return CONNECTOR_STYLES[type] || { emoji: '🔌', color: '#6b7280' };
}

// Leyenda de tipos de conectores
function ConnectorTypeLegend({ t, connectorTypes, connectorFilters, onToggleConnectorFilter, mapRef }) {
  const [collapsed, setCollapsed] = useState(true);
  const { dragRef, onDragStart, dragStyle, dragging, didDragRef } = useDraggable(mapRef);
  const displayConnectorTypes = (connectorTypes && connectorTypes.length > 0)
    ? connectorTypes
    : Object.keys(CONNECTOR_STYLES);
  return (
    <div ref={dragRef} className="map-connector-legend interactive" style={{ ...dragStyle, cursor: dragging ? 'grabbing' : undefined }}>
      <div className="legend-header-row" onMouseDown={onDragStart} onTouchStart={onDragStart} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'grab', userSelect: 'none' }}>
        <span style={{ color: '#9ca3af', fontSize: '0.75em', cursor: 'grab' }}>⋮⋮</span>
        <div className="legend-title" onClick={() => { if (!didDragRef.current) setCollapsed(v => !v); }} style={{ cursor: 'pointer', flex: 1, marginBottom: 0 }}>
          🔌 {t('connectors.legend')} {collapsed ? '▸' : '▾'}
        </div>
      </div>
      {!collapsed && displayConnectorTypes.map((type) => {
        const active = connectorFilters.size === 0 || connectorFilters.has(type);
        const style = getConnectorStyle(type);
        return (
          <div
            key={type}
            className={`legend-item clickable${active ? '' : ' inactive'}`}
            onClick={() => onToggleConnectorFilter(type)}
            title={active ? t('legend.clickToHide') : t('legend.clickToShow')}
          >
            <span className="legend-dot" style={{ background: style.color, opacity: active ? 1 : 0.2 }} />
            <span style={{ color: active ? 'inherit' : '#6b7280', fontSize: '0.85em' }}>{type}</span>
          </div>
        );
      })}
      {!collapsed && connectorFilters.size > 0 && (
        <div
          className="legend-item clickable"
          onClick={() => { /* Clear all filters by toggling each active one off */ connectorFilters.forEach(t => onToggleConnectorFilter(t)); }}
          style={{ marginTop: 4, justifyContent: 'center', color: '#2563eb', fontWeight: 600, fontSize: '0.8em' }}
        >
          ↺ {t('connectors.showAll')}
        </div>
      )}
    </div>
  );
}

// Leyenda de operadores de cargadores con favoritos
function OperatorLegend({ t, operators, operatorFilters, onToggleOperatorFilter, defaultCollapsed = true, mapRef }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const { dragRef, onDragStart, dragStyle, dragging, didDragRef } = useDraggable(mapRef);
  const [favOperators, setFavOperators] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('evplanner-fav-operators'));
      if (saved && Array.isArray(saved)) return new Set(saved);
    } catch {}
    return null; // null = use defaults
  });

  const isFavorite = useCallback((op) => {
    if (favOperators === null) return isDefaultFavOperator(op);
    return favOperators.has(op);
  }, [favOperators]);

  const toggleFavOperator = useCallback((op) => {
    setFavOperators(prev => {
      let set;
      if (prev === null) {
        set = new Set(operators.filter(isDefaultFavOperator));
      } else {
        set = new Set(prev);
      }
      if (set.has(op)) set.delete(op);
      else set.add(op);
      localStorage.setItem('evplanner-fav-operators', JSON.stringify([...set]));
      return set;
    });
  }, [operators]);

  const displayOperators = (operators && operators.length > 0)
    ? operators
    : DEFAULT_FAVORITE_OPERATORS_DISPLAY;

  const favorites = displayOperators.filter(op => isFavorite(op)).sort((a, b) => a.localeCompare(b));
  const nonFavorites = displayOperators.filter(op => !isFavorite(op)).sort((a, b) => a.localeCompare(b));

  return (
    <div ref={dragRef} className="map-operator-legend interactive" style={{ ...dragStyle, cursor: dragging ? 'grabbing' : undefined }}>
      <div className="legend-header-row" onMouseDown={onDragStart} onTouchStart={onDragStart} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'grab', userSelect: 'none' }}>
        <span style={{ color: '#9ca3af', fontSize: '0.75em', cursor: 'grab' }}>⋮⋮</span>
        <div className="legend-title" onClick={() => { if (!didDragRef.current) setCollapsed(v => !v); }} style={{ cursor: 'pointer', flex: 1, marginBottom: 0 }}>
          🏢 {t('operators.legend')} {collapsed ? '▸' : '▾'}
        </div>
      </div>
      {!collapsed && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 4, justifyContent: 'center' }}>
            <span
              className="legend-item clickable"
              onClick={() => { displayOperators.forEach(op => { if (!operatorFilters.has(op)) onToggleOperatorFilter(op); }); }}
              style={{ color: '#2563eb', fontWeight: 600, fontSize: '0.75em', cursor: 'pointer' }}
            >
              {t('operators.selectAll')}
            </span>
            <span style={{ color: '#9ca3af' }}>|</span>
            <span
              className="legend-item clickable"
              onClick={() => { operatorFilters.forEach(op => onToggleOperatorFilter(op)); }}
              style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.75em', cursor: 'pointer' }}
            >
              {t('operators.deselectAll')}
            </span>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {favorites.map((op) => {
              const active = operatorFilters.size === 0 || operatorFilters.has(op);
              const letter = getOperatorLetter(op);
              return (
                <div
                  key={op}
                  className={`legend-item clickable${active ? '' : ' inactive'}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavOperator(op); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.85em', color: '#f59e0b', lineHeight: 1, flexShrink: 0, width: 16, height: 16, textAlign: 'center' }}
                    title="★"
                  >★</button>
                  <span
                    onClick={() => onToggleOperatorFilter(op)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flex: 1, minWidth: 0 }}
                    title={active ? t('legend.clickToHide') : t('legend.clickToShow')}
                  >
                    <span className="legend-dot" style={{ background: '#6b7280', opacity: active ? 1 : 0.2 }} />
                    <span style={{ color: active ? 'inherit' : '#6b7280', fontSize: '0.82em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {letter && <strong style={{ marginRight: 2 }}>[{letter}]</strong>}{op}
                    </span>
                  </span>
                </div>
              );
            })}
            {favorites.length > 0 && nonFavorites.length > 0 && (
              <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #d1d5db' }} />
            )}
            {nonFavorites.map((op) => {
              const active = operatorFilters.size === 0 || operatorFilters.has(op);
              const letter = getOperatorLetter(op);
              return (
                <div
                  key={op}
                  className={`legend-item clickable${active ? '' : ' inactive'}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavOperator(op); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.85em', color: '#d1d5db', lineHeight: 1, flexShrink: 0, width: 16, height: 16, textAlign: 'center' }}
                    title="☆"
                  >☆</button>
                  <span
                    onClick={() => onToggleOperatorFilter(op)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flex: 1, minWidth: 0 }}
                    title={active ? t('legend.clickToHide') : t('legend.clickToShow')}
                  >
                    <span className="legend-dot" style={{ background: '#6b7280', opacity: active ? 1 : 0.2 }} />
                    <span style={{ color: active ? 'inherit' : '#6b7280', fontSize: '0.82em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {letter && <strong style={{ marginRight: 2 }}>[{letter}]</strong>}{op}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Popup del cargador con minimizar, estrella favorito, añadir parada
function ChargerPopupContent({
  charger, isFav, onToggleFav, onAddStop, isAlreadyStop,
  deviationKm, distToPrevPoint, distToNextPoint,
}) {
  const { t } = useI18n();
  const [minimized, setMinimized] = useState(false);
  const [showPriceInfo, setShowPriceInfo] = useState(false);
  const popupRef = useRef(null);
  const map = useMap();
  const grouped = groupConnectors(charger.connectors);

  // Reposition popup when minimized/expanded to keep it centered above marker
  useEffect(() => {
    if (!popupRef.current || !map) return;
    const raf = requestAnimationFrame(() => {
      // Find the open popup on the map and call update()
      map.eachLayer((layer) => {
        if (layer.getPopup && layer.getPopup() && layer.getPopup().isOpen()) {
          const popupEl = layer.getPopup().getElement();
          if (popupEl && popupEl.contains(popupRef.current)) {
            layer.getPopup().update();
          }
        }
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [minimized, map]);

  // Potencia máx CCS2/Type2 para vista minimizada
  const mainPower = useMemo(() => {
    const priority = ['CCS2', 'CCS', 'TYPE2', 'TYPE 2', 'MENNEKES'];
    const match = (charger.connectors || [])
      .filter(c => priority.some(p => (c.type || '').toUpperCase().replace(/ /g, '').includes(p.replace(/ /g, ''))))
      .sort((a, b) => (b.power || 0) - (a.power || 0))[0];
    return match?.power || charger.maxPower;
  }, [charger]);

  const address = charger.address
    ? (typeof charger.address === 'string'
        ? charger.address
        : [charger.address.street, charger.address.city].filter(Boolean).join(', '))
    : '';

  const btnStyle = {
    background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '3px',
    cursor: 'pointer', fontSize: '10px', padding: '1px 3px', lineHeight: '1',
    color: '#374151', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: '16px', minHeight: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  };
  const starStyle = {
    ...btnStyle,
    color: isFav ? '#f59e0b' : '#6b7280',
    background: isFav ? '#fef3c7' : '#f3f4f6',
    borderColor: isFav ? '#fbbf24' : '#d1d5db',
    fontSize: '11px',
  };

  if (minimized) {
    return (
      <div className="charger-popup-minimized" ref={popupRef}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
          <strong style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85em' }}>
            {charger.operator || t('chargers.unknown')}
          </strong>
          <button onClick={() => onToggleFav(charger)} style={{...starStyle, minWidth: '14px', minHeight: '14px', fontSize: '9px', padding: '0px 2px'}}
            title={isFav ? t('chargers.unfavorite') : t('chargers.favorite')}>
            {isFav ? '★' : '☆'}
          </button>
          <button onClick={() => setMinimized(false)} title={t('map.expand')} style={{...btnStyle, minWidth: '14px', minHeight: '14px', fontSize: '9px', padding: '0px 2px'}}>
            □
          </button>
          <button onClick={() => map.closePopup()} title={t('form.close')} style={{...btnStyle, minWidth: '14px', minHeight: '14px', fontSize: '9px', padding: '0px 2px', color: '#dc2626'}}>
            ✕
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {mainPower && (
            <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '0.85em' }}>
              ⚡{Math.round(mainPower)}kW
            </span>
          )}
          <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '0.85em' }}>
            {charger.usageCost || ''}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div ref={popupRef} style={{ fontSize: '0.82em', lineHeight: 1.35, minWidth: 180, maxWidth: 240, paddingTop: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <strong style={{ fontSize: '0.95em', flex: 1, marginRight: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {charger.operator || t('chargers.unknown')}
        </strong>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <button onClick={() => onToggleFav(charger)}
            title={isFav ? t('chargers.unfavorite') : t('chargers.favorite')}
            style={{...starStyle, minWidth: '22px', minHeight: '20px', padding: '1px 4px', fontSize: '13px'}}>
            {isFav ? '★' : '☆'}
          </button>
          <button onClick={() => setMinimized(true)} title={t('map.minimize')} style={{...btnStyle, minWidth: '22px', minHeight: '20px', padding: '1px 4px', fontSize: '12px'}}>
            —
          </button>
          <button onClick={() => map.closePopup()} title={t('form.close')} style={{...btnStyle, minWidth: '22px', minHeight: '20px', padding: '1px 4px', fontSize: '12px', color: '#dc2626'}}>
            ✕
          </button>
        </div>
      </div>
      <div style={{ margin: '2px 0', padding: '2px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
        {grouped.map((g, gi) => (
          <span key={gi} style={{
            display: 'inline-block', padding: '1px 4px', margin: '1px',
            backgroundColor: g.power > 150 ? '#ede9fe' : g.power > 50 ? '#fff7ed' : '#f0fdf4',
            borderRadius: '3px', fontSize: '0.82em',
          }}>
            {g.type}{g.power > 0 ? ` ${g.power}kW` : ''} ({g.count})
          </span>
        ))}
      </div>
      {charger.maxPower && (
        <div style={{ fontSize: '0.9em' }}><span style={{ color: '#2563eb', fontWeight: 'bold' }}>⚡{charger.maxPower}kW</span></div>
      )}
      <div style={{ fontSize: '0.9em', display: 'flex', alignItems: 'center', gap: 3 }}>
        <span style={{ color: '#059669', fontWeight: 'bold' }}>💰 {charger.usageCost || '€--/kWh'}</span>
        <span
          title={t('chargers.priceDisclaimer')}
          onClick={() => setShowPriceInfo(v => !v)}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: '50%', background: '#e5e7eb', color: '#6b7280', fontSize: '0.7em', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0 }}
        >i</span>
      </div>
      {showPriceInfo && (
        <div style={{ fontSize: '0.75em', color: '#6b7280', background: '#f9fafb', padding: '3px 6px', borderRadius: 4, marginTop: 2, lineHeight: 1.3 }}>
          {t('chargers.priceDisclaimer')}
        </div>
      )}
      {address && <div style={{ color: '#666', fontSize: '0.8em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{address}</div>}
      {deviationKm != null && (
        <div style={{ marginTop: '2px', padding: '2px 5px', backgroundColor: '#f0f4ff', borderRadius: '3px', fontSize: '0.78em', lineHeight: 1.4 }}>
          <span style={{ color: '#dc2626' }}>↗{deviationKm.toFixed(1)}km</span>
          {distToPrevPoint != null && <span style={{ color: '#2563eb', marginLeft: 4 }}>←{distToPrevPoint.toFixed(1)}km</span>}
          {distToNextPoint != null && <span style={{ color: '#059669', marginLeft: 4 }}>→{distToNextPoint.toFixed(1)}km</span>}
        </div>
      )}
      {onAddStop && !isAlreadyStop && (
        <button onClick={onAddStop}
          style={{ marginTop: '4px', width: '100%', padding: '3px 6px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8em' }}>
          🛑 {t('map.addAsStop')}
        </button>
      )}
      {isAlreadyStop && (
        <div style={{ marginTop: '3px', color: '#059669', fontWeight: 'bold', textAlign: 'center', fontSize: '0.8em' }}>✅ {t('map.alreadyStop')}</div>
      )}
    </div>
  );
}

// Componente para tooltip de distancia al pasar ratón sobre la ruta
function RouteDistanceTooltip({ routePositions, totalDistanceKm, pinnedMarks, routeStops, onAddPin, onRemovePin }) {
  const map = useMap();
  const tooltipRef = useRef(null);
  const clickTooltipRef = useRef(null);
  const cumDistsRef = useRef([]);
  const MAX_SNAP_DIST = 30;

  useEffect(() => {
    if (routePositions.length > 1) {
      cumDistsRef.current = computeCumulativeDistances(routePositions);
    }
  }, [routePositions]);

  // Combine pins and stops into sorted reference points
  const allMarkedPoints = useMemo(() => {
    const points = [
      ...pinnedMarks.map(p => ({ km: p.km, type: 'pin' })),
      ...routeStops.map(s => ({ km: s.km, type: 'stop' })),
    ];
    return points.sort((a, b) => a.km - b.km);
  }, [pinnedMarks, routeStops]);

  useEffect(() => {
    if (!map || routePositions.length < 2) return;

    const tooltip = L.tooltip({ permanent: false, direction: 'top', offset: [0, -10], className: 'route-distance-tooltip' });
    tooltipRef.current = tooltip;

    const isNearRoute = (latlng) => {
      const point = map.latLngToContainerPoint(latlng);
      for (let i = 0; i < routePositions.length - 1; i++) {
        const a = map.latLngToContainerPoint(L.latLng(routePositions[i]));
        const b = map.latLngToContainerPoint(L.latLng(routePositions[i + 1]));
        const dist = L.LineUtil.pointToSegmentDistance(point, a, b);
        if (dist < MAX_SNAP_DIST) return true;
      }
      return false;
    };

    const buildContent = (km) => {
      const totalKm = totalDistanceKm || cumDistsRef.current[cumDistsRef.current.length - 1] || 0;
      const remaining = Math.max(0, totalKm - km);
      const { prevKm, nextKm } = findPrevNextKm(km, allMarkedPoints, totalKm);
      const fromPrevInt = Math.round(km - prevKm);
      const toNextInt = Math.round(nextKm - km);
      let content = `<div style="text-align:center;line-height:1.5"><div style="font-weight:700;font-size:12px">${Math.round(km)}km &ndash; ${Math.round(remaining)}km</div>`;
      if (allMarkedPoints.length > 0) {
        content += `<div style="font-size:11px;opacity:0.9">* ${fromPrevInt}km * ${toNextInt}km *</div>`;
      }
      content += `</div>`;
      return content;
    };

    const handleMouseMove = (e) => {
      if (!isNearRoute(e.latlng)) {
        if (map.hasLayer(tooltip)) map.removeLayer(tooltip);
        return;
      }
      const km = getDistanceAlongRoute(e.latlng, routePositions, cumDistsRef.current);
      tooltip.setLatLng(e.latlng);
      tooltip.setContent(buildContent(km));
      if (!map.hasLayer(tooltip)) tooltip.addTo(map);
    };

    const handleMouseOut = () => {
      if (map.hasLayer(tooltip)) map.removeLayer(tooltip);
    };

    // Single click on route shows persistent tooltip (for mobile/touch)
    const handleClick = (e) => {
      if (!isNearRoute(e.latlng)) {
        if (clickTooltipRef.current && map.hasLayer(clickTooltipRef.current)) {
          map.removeLayer(clickTooltipRef.current);
          clickTooltipRef.current = null;
        }
        return;
      }
      const km = getDistanceAlongRoute(e.latlng, routePositions, cumDistsRef.current);
      // Remove previous click tooltip if exists
      if (clickTooltipRef.current && map.hasLayer(clickTooltipRef.current)) {
        map.removeLayer(clickTooltipRef.current);
      }
      const clickTip = L.tooltip({ permanent: true, direction: 'top', offset: [0, -10], className: 'route-distance-tooltip route-distance-tooltip-click' });
      clickTip.setLatLng(e.latlng);
      clickTip.setContent(buildContent(km));
      clickTip.addTo(map);
      clickTooltipRef.current = clickTip;
      // Auto-remove after 4 seconds
      setTimeout(() => {
        if (clickTooltipRef.current && map.hasLayer(clickTooltipRef.current)) {
          map.removeLayer(clickTooltipRef.current);
          clickTooltipRef.current = null;
        }
      }, 4000);
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseOut);
    map.on('click', handleClick);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseOut);
      map.off('click', handleClick);
      if (map.hasLayer(tooltip)) map.removeLayer(tooltip);
      if (clickTooltipRef.current && map.hasLayer(clickTooltipRef.current)) {
        map.removeLayer(clickTooltipRef.current);
      }
    };
  }, [map, routePositions, totalDistanceKm, allMarkedPoints]);

  useMapEvents({
    dblclick(e) {
      if (routePositions.length < 2) return;
      const point = map.latLngToContainerPoint(e.latlng);
      let nearRoute = false;
      for (let i = 0; i < routePositions.length - 1; i++) {
        const a = map.latLngToContainerPoint(L.latLng(routePositions[i]));
        const b = map.latLngToContainerPoint(L.latLng(routePositions[i + 1]));
        if (L.LineUtil.pointToSegmentDistance(point, a, b) < MAX_SNAP_DIST) { nearRoute = true; break; }
      }
      if (!nearRoute) return;

      const cumDists = cumDistsRef.current;
      const km = getDistanceAlongRoute(e.latlng, routePositions, cumDists);

      const existingIdx = pinnedMarks.findIndex(p => Math.abs(p.km - km) < 2);
      if (existingIdx >= 0) {
        onRemovePin(existingIdx);
      } else {
        let minDist = Infinity;
        let bestLatLng = e.latlng;
        for (let i = 0; i < routePositions.length - 1; i++) {
          const a = L.latLng(routePositions[i]);
          const b = L.latLng(routePositions[i + 1]);
          const proj = projectPointOnSegment(e.latlng, a, b);
          const d = e.latlng.distanceTo(proj);
          if (d < minDist) { minDist = d; bestLatLng = proj; }
        }
        const totalKm = cumDists[cumDists.length - 1] || 0;
        onAddPin({ lat: bestLatLng.lat, lng: bestLatLng.lng, km: Math.round(km * 10) / 10, totalKm });
      }
    }
  });

  return null;
}

export default function RouteMap({
  route, origin, destination,
  previewOrigin, previewDestination,
  routeChargers = [], totalDistanceKm, filters,
  onAddRouteStop, routeStops = [],
  maxDeviation = 10,
  favoriteChargers = [], onToggleFavorite,
  viaPointMode = false, onAddMapViaPoint, mapViaPoints = [], onRemoveMapViaPoint,
  onPinsChange,
  mapMode, onSetOrigin, onSetDestination,
  kmPointsFromTab = [],
  onRemoveTabKmPoint,
  powerRangeConfig,
  updatePowerRange, savePowerRangeConfig, resetPowerRangeConfig, configSaved, currentUser,
  maxDeviationValue, onMaxDeviationChange,
  onShowHelp,
  mapLoading, onRefreshSearch,
  onDragOrigin, onDragDestination,
  evCarConfig, onSuggestStops,
  selectedAltRoute,
  onOperatorFiltersChange,
  onConnectorFiltersChange,
  onLegendFiltersChange,
  onVisibleChargersChange,
}) {
  const { t } = useI18n();
  const ranges = powerRangeConfig && powerRangeConfig.length === 4 ? powerRangeConfig : DEFAULT_POWER_RANGES;
  const [pinnedMarks, setPinnedMarks] = useState([]);
  const [pinChargers, setPinChargers] = useState({}); // chargers per pin {pinKey: [chargers]}
  const [loadingPinChargers, setLoadingPinChargers] = useState({});

  // Bubble overlay toggles for price / kWh
  const [showBubblePrice, setShowBubblePrice] = useState(false);
  const [showBubbleKwh, setShowBubbleKwh] = useState(false);
  // Track which popups are open (persisted across zoom/cluster changes)
  const [openPopupChargerIds, setOpenPopupChargerIds] = useState(new Set());
  const openPopupChargerIdsRef = useRef(openPopupChargerIds);
  useEffect(() => { openPopupChargerIdsRef.current = openPopupChargerIds; }, [openPopupChargerIds]);
  // Cluster zoom threshold (default 10, min 8)
  const [clusterZoom, setClusterZoom] = useState('10');
  const [localClusterZoom, setLocalClusterZoom] = useState('10');
  const commitClusterZoom = useCallback(() => {
    let val = Number(localClusterZoom) || 10;
    if (val < 8) val = 8;
    setLocalClusterZoom(String(val));
    setClusterZoom(String(val));
  }, [localClusterZoom]);

  // Info tip (double-click hint) visibility and drag
  const [showInfoTip, setShowInfoTip] = useState(true);
  const leafletMapRef = useRef(null);
  const infoTipDrag = useDraggable(leafletMapRef);

  // Local search radius input (clearable, commits on Enter/blur/refresh)
  const [localRadius, setLocalRadius] = useState(String(maxDeviationValue || 5));
  const commitRadius = useCallback(() => {
    let val = localRadius.trim() === '' ? 5 : Number(localRadius) || 5;
    if (val < 1) val = 1;
    if (val > 100) val = 100;
    setLocalRadius(String(val));
    if (onMaxDeviationChange) onMaxDeviationChange(val);
  }, [localRadius, onMaxDeviationChange]);

  // Loading overlay with 1-second delay (defined after loadingQueryChargers below)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // Exploration pins (double-click outside route)
  const [explorePins, setExplorePins] = useState([]);
  const [explorePinChargers, setExplorePinChargers] = useState({});
  const [loadingExplorePinChargers, setLoadingExplorePinChargers] = useState({});

  // Legend filter state (which power categories are visible)
  const [legendFilters, setLegendFilters] = useState(() => new Set([0, 1, 2, 3]));

  // Connector type legend filter state
  const [connectorFilters, setConnectorFilters] = useState(() => new Set()); // empty = show all

  // Operator legend filter state
  const [operatorFilters, setOperatorFilters] = useState(() => new Set()); // empty = show all

  // Charger Query Mode state
  const [chargerQueryMode, setChargerQueryMode] = useState(false);
  const [allDbOperators, setAllDbOperators] = useState([]);
  const [queryModeChargers, setQueryModeChargers] = useState([]);
  const [queryOperatorFilters, setQueryOperatorFilters] = useState(() => new Set());
  const [loadingQueryChargers, setLoadingQueryChargers] = useState(false);

  // Loading overlay with 1-second delay
  const isAnyLoading = mapLoading || loadingQueryChargers;
  useEffect(() => {
    if (!isAnyLoading) { setShowLoadingOverlay(false); return; }
    const timer = setTimeout(() => setShowLoadingOverlay(true), 1000);
    return () => clearTimeout(timer);
  }, [isAnyLoading]);

  // Fetch all operators from DB when entering query mode (with retry)
  useEffect(() => {
    if (!chargerQueryMode) return;
    if (allDbOperators.length > 0) return;
    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 5;
    const fetchOperators = () => {
      fetch(`${API_URL}/api/chargers/operators`)
        .then(r => r.json())
        .then(data => {
          if (cancelled) return;
          if (data.success && data.data && data.data.length > 0) {
            setAllDbOperators(data.data);
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(fetchOperators, 3000 * retryCount);
          }
        })
        .catch(() => {
          if (!cancelled && retryCount < maxRetries) {
            retryCount++;
            setTimeout(fetchOperators, 3000 * retryCount);
          }
        });
    };
    fetchOperators();
    return () => { cancelled = true; };
  }, [chargerQueryMode, allDbOperators.length]);

  // Fetch chargers when query operators change
  useEffect(() => {
    if (!chargerQueryMode || queryOperatorFilters.size === 0) {
      setQueryModeChargers([]);
      return;
    }
    setLoadingQueryChargers(true);
    const ops = Array.from(queryOperatorFilters).join(',');
    fetch(`${API_URL}/api/chargers/by-operator?operators=${encodeURIComponent(ops)}`)
      .then(r => r.json())
      .then(data => { if (data.success) setQueryModeChargers(data.data || []); })
      .catch(() => {})
      .finally(() => setLoadingQueryChargers(false));
  }, [chargerQueryMode, queryOperatorFilters]);

  const handleToggleQueryOperator = useCallback((op) => {
    setQueryOperatorFilters(prev => {
      const next = new Set(prev);
      if (next.has(op)) next.delete(op);
      else next.add(op);
      return next;
    });
  }, []);

  // Convertir coordenadas de GeoJSON [lng, lat] a Leaflet [lat, lng]
  const routePositions = useMemo(() => {
    if (!route?.geometry?.coordinates) return [];
    return route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
  }, [route]);

  const cumDists = useMemo(() => {
    if (routePositions.length > 1) return computeCumulativeDistances(routePositions);
    return [];
  }, [routePositions]);

  // Ref para tracking de chargers ya fetcheados para tabKmPins
  const fetchedTabKmKeysRef = useRef(new Set());

  // Reset pins when route changes
  useEffect(() => { setPinnedMarks([]); setPinChargers({}); fetchedTabKmKeysRef.current = new Set(); }, [route]);

  // Notifica al padre cuando cambian los pins
  useEffect(() => { if (onPinsChange) onPinsChange(pinnedMarks); }, [pinnedMarks, onPinsChange]);

  // All marked points (pins + stops) sorted by km
  const allMarkedPoints = useMemo(() => {
    const points = [
      ...pinnedMarks.map(p => ({ km: p.km, type: 'pin' })),
      ...routeStops.map(s => ({ km: s.km, type: 'stop' })),
    ];
    return points.sort((a, b) => a.km - b.km);
  }, [pinnedMarks, routeStops]);

  // Fetch chargers near a pin point (with retry on empty result)
  const fetchChargersForPin = useCallback(async (pin) => {
    const pinKey = `${pin.lat.toFixed(5)}-${pin.lng.toFixed(5)}`;
    setLoadingPinChargers(prev => ({ ...prev, [pinKey]: true }));
    const doFetch = async (attempt) => {
      try {
        const params = new URLSearchParams({ lat: pin.lat, lng: pin.lng, radius: String(maxDeviation || 10) });
        if (filters?.currentTypes?.length) params.set('currentTypes', filters.currentTypes.join(','));
        if (filters?.connectors?.length) params.set('connectors', filters.connectors.join(','));
        if (filters?.powerRanges?.length) params.set('powerRanges', filters.powerRanges.join(','));
        
        const response = await fetch(`${API_URL}/api/chargers?${params}`);
        const data = await response.json();
        if (data.success && data.data && data.data.length > 0) {
          setPinChargers(prev => ({ ...prev, [pinKey]: data.data }));
          return true;
        }
        return false;
      } catch (err) {
        console.error('Error fetching pin chargers:', err);
        return false;
      }
    };
    let success = await doFetch(0);
    if (!success) {
      // Retry up to 3 times with increasing delay
      for (let i = 1; i <= 3 && !success; i++) {
        await new Promise(r => setTimeout(r, 3000 * i));
        success = await doFetch(i);
      }
      if (!success) setPinChargers(prev => ({ ...prev, [pinKey]: [] }));
    }
    setLoadingPinChargers(prev => ({ ...prev, [pinKey]: false }));
  }, [filters, maxDeviation]);

  const handleAddPin = useCallback((pin) => {
    setPinnedMarks(prev => [...prev, pin]);
    fetchChargersForPin(pin);
  }, [fetchChargersForPin]);

  const handleRemovePin = useCallback((idx) => {
    setPinnedMarks(prev => {
      const removed = prev[idx];
      if (removed) {
        const pinKey = `${removed.lat.toFixed(5)}-${removed.lng.toFixed(5)}`;
        setPinChargers(pc => { const n = { ...pc }; delete n[pinKey]; return n; });
      }
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  // Exploration pin handlers
  const fetchExplorePinChargers = useCallback(async (pin) => {
    const pinKey = `${pin.lat.toFixed(5)}-${pin.lng.toFixed(5)}`;
    setLoadingExplorePinChargers(prev => ({ ...prev, [pinKey]: true }));
    const doFetch = async () => {
      try {
        const params = new URLSearchParams({ lat: pin.lat, lng: pin.lng, radius: String(maxDeviation || 10) });
        if (filters?.currentTypes?.length) params.set('currentTypes', filters.currentTypes.join(','));
        if (filters?.connectors?.length) params.set('connectors', filters.connectors.join(','));
        if (filters?.powerRanges?.length) params.set('powerRanges', filters.powerRanges.join(','));
        const response = await fetch(`${API_URL}/api/chargers?${params}`);
        const data = await response.json();
        if (data.success && data.data && data.data.length > 0) {
          setExplorePinChargers(prev => ({ ...prev, [pinKey]: data.data }));
          return true;
        }
        return false;
      } catch (err) {
        console.error('Error fetching explore pin chargers:', err);
        return false;
      }
    };
    let success = await doFetch();
    if (!success) {
      for (let i = 1; i <= 3 && !success; i++) {
        await new Promise(r => setTimeout(r, 3000 * i));
        success = await doFetch();
      }
      if (!success) setExplorePinChargers(prev => ({ ...prev, [pinKey]: [] }));
    }
    setLoadingExplorePinChargers(prev => ({ ...prev, [pinKey]: false }));
  }, [filters, maxDeviation]);

  const handleAddExplorePin = useCallback((pin) => {
    setExplorePins(prev => [...prev, pin]);
    fetchExplorePinChargers(pin);
  }, [fetchExplorePinChargers]);

  const handleRemoveExplorePin = useCallback((idx) => {
    setExplorePins(prev => {
      const removed = prev[idx];
      if (removed) {
        const pinKey = `${removed.lat.toFixed(5)}-${removed.lng.toFixed(5)}`;
        setExplorePinChargers(pc => { const n = { ...pc }; delete n[pinKey]; return n; });
      }
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const handleToggleLegendFilter = useCallback((idx) => {
    setLegendFilters(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      if (onLegendFiltersChange) onLegendFiltersChange(next);
      return next;
    });
  }, [onLegendFiltersChange]);

  const handleToggleConnectorFilter = useCallback((connType) => {
    setConnectorFilters(prev => {
      const next = new Set(prev);
      if (next.has(connType)) next.delete(connType);
      else next.add(connType);
      if (onConnectorFiltersChange) onConnectorFiltersChange(next);
      return next;
    });
  }, [onConnectorFiltersChange]);

  const handleToggleOperatorFilter = useCallback((operator) => {
    setOperatorFilters(prev => {
      const next = new Set(prev);
      if (next.has(operator)) next.delete(operator);
      else next.add(operator);
      if (onOperatorFiltersChange) onOperatorFiltersChange(next);
      return next;
    });
  }, [onOperatorFiltersChange]);

  const bounds = useMemo(() => {
    if (routePositions.length > 0) return routePositions;
    if (origin && destination) return [[origin.lat, origin.lng], [destination.lat, destination.lng]];
    // Preview markers bounds
    const pts = [];
    if (previewOrigin) pts.push([previewOrigin.lat, previewOrigin.lng]);
    if (previewDestination) pts.push([previewDestination.lat, previewDestination.lng]);
    if (pts.length >= 2) return pts;
    return null;
  }, [routePositions, origin, destination, previewOrigin, previewDestination]);

  const center = useMemo(() => {
    if (origin && destination) return [(origin.lat + destination.lat) / 2, (origin.lng + destination.lng) / 2];
    if (previewOrigin && previewDestination) return [(previewOrigin.lat + previewDestination.lat) / 2, (previewOrigin.lng + previewDestination.lng) / 2];
    if (previewOrigin) return [previewOrigin.lat, previewOrigin.lng];
    if (previewDestination) return [previewDestination.lat, previewDestination.lng];
    return [40.4168, -3.7038]; // Madrid default
  }, [origin, destination, previewOrigin, previewDestination]);

  // Convert kmPointsFromTab into map positions along the route
  const tabKmPins = useMemo(() => {
    if (!routePositions.length || !cumDists.length || !kmPointsFromTab.length) return [];
    const totalKm = cumDists[cumDists.length - 1] || 0;
    return kmPointsFromTab
      .map(k => Number(k))
      .filter(k => !isNaN(k) && k > 0 && k < totalKm)
      .filter(kmVal => !pinnedMarks.some(p => Math.abs(p.km - kmVal) < 1)) // skip if already a pin
      .map(kmVal => {
        // Find the position on the route at this km
        for (let i = 0; i < cumDists.length - 1; i++) {
          if (cumDists[i + 1] >= kmVal) {
            const ratio = (kmVal - cumDists[i]) / (cumDists[i + 1] - cumDists[i]);
            const lat = routePositions[i][0] + ratio * (routePositions[i + 1][0] - routePositions[i][0]);
            const lng = routePositions[i][1] + ratio * (routePositions[i + 1][1] - routePositions[i][1]);
            return { lat, lng, km: kmVal };
          }
        }
        return null;
      })
      .filter(Boolean);
  }, [routePositions, cumDists, kmPointsFromTab, pinnedMarks]);

  // Auto-fetch chargers for tabKmPins
  useEffect(() => {
    tabKmPins.forEach(pin => {
      const pinKey = `${pin.lat.toFixed(5)}-${pin.lng.toFixed(5)}`;
      if (!fetchedTabKmKeysRef.current.has(pinKey)) {
        fetchedTabKmKeysRef.current.add(pinKey);
        fetchChargersForPin(pin);
      }
    });
  }, [tabKmPins, fetchChargersForPin]);

  // Combine routeChargers + pinChargers + explorePinChargers into one array, de-duped
  const allChargers = useMemo(() => {
    // In query mode, use chargers fetched by operator
    if (chargerQueryMode) return queryModeChargers;

    const seen = new Set();
    const result = [];
    for (const c of routeChargers) {
      const key = c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`;
      if (!seen.has(key)) { seen.add(key); result.push(c); }
    }
    for (const chargers of Object.values(pinChargers)) {
      for (const c of chargers) {
        const key = c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`;
        if (!seen.has(key)) { seen.add(key); result.push(c); }
      }
    }
    for (const chargers of Object.values(explorePinChargers)) {
      for (const c of chargers) {
        const key = c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`;
        if (!seen.has(key)) { seen.add(key); result.push(c); }
      }
    }
    return result;
  }, [routeChargers, pinChargers, explorePinChargers, chargerQueryMode, queryModeChargers]);

  // Extract unique connector types from all chargers
  const connectorTypes = useMemo(() => {
    const types = new Set();
    for (const c of allChargers) {
      if (c.connectors) {
        for (const conn of c.connectors) {
          if (conn.type) types.add(conn.type);
        }
      }
    }
    return Array.from(types).sort();
  }, [allChargers]);

  // Extract unique operators from all chargers
  const operatorList = useMemo(() => {
    const ops = new Set();
    for (const c of allChargers) {
      if (c.operator) ops.add(c.operator);
    }
    return Array.from(ops).sort();
  }, [allChargers]);

  // Filter chargers based on legend power range filters, connector type filters AND operator filters
  const visibleChargers = useMemo(() => {
    let filtered = allChargers;
    // Power filter
    if (legendFilters.size !== ranges.length) {
      filtered = filtered.filter(charger => {
        const power = charger.maxPower || 0;
        return ranges.some((range, idx) => {
          if (!legendFilters.has(idx)) return false;
          if (range.type === 'exact') return Math.abs(power - range.value) < 0.5;
          return power >= range.min && power <= range.max;
        });
      });
    }
    // Connector type filter (if any selected, show only those)
    if (connectorFilters.size > 0) {
      filtered = filtered.filter(charger => {
        if (!charger.connectors || charger.connectors.length === 0) return false;
        return charger.connectors.some(conn => connectorFilters.has(conn.type));
      });
    }
    // Operator filter (if any selected, show only those)
    if (operatorFilters.size > 0) {
      filtered = filtered.filter(charger => {
        return charger.operator && operatorFilters.has(charger.operator);
      });
    }
    return filtered;
  }, [allChargers, legendFilters, ranges, connectorFilters, operatorFilters]);

  // Notify parent component of the current set of chargers drawn on the map
  // so that the RoutesPopup can calculate routes using ONLY the chargers the
  // user has actually visualised (from stop points, exploration pins, etc.).
  useEffect(() => {
    if (onVisibleChargersChange) {
      onVisibleChargersChange(visibleChargers);
    }
  }, [visibleChargers, onVisibleChargersChange]);

  return (
    <div style={{ width: '100%' }}>
      {/* Selector Modo Ruta / Cargadores */}
      <div className="map-mode-toggle-bar">
        <span className="map-mode-label">{t('map.modeLabel')}:</span>
        <span
          className={`map-mode-option${!chargerQueryMode ? ' active' : ''}`}
          onClick={() => setChargerQueryMode(false)}
        >
          {t('map.modeRoute')}
        </span>
        <span className="map-mode-separator">/</span>
        <span
          className={`map-mode-option${chargerQueryMode ? ' active' : ''}`}
          onClick={() => setChargerQueryMode(true)}
        >
          {t('map.modeChargers')}
          {chargerQueryMode && loadingQueryChargers && <span style={{ marginLeft: 4, fontSize: '0.8em' }}>⏳</span>}
        </span>
      </div>
      {/* Mostrar: precio / kW — right-justified below mode bar */}
      <div className="map-show-bar">
        <div className="map-bubble-toggles">
          <span style={{ color: '#6b7280', fontWeight: 500, fontSize: '0.92em', marginRight: 2 }}>{t('map.showLabel')}</span>
          <span
            className={`map-bubble-toggle${showBubblePrice ? ' active' : ''}`}
            onClick={() => setShowBubblePrice(v => !v)}
          >{t('map.showPrice')}</span>
          <span className="map-bubble-sep">/</span>
          <span
            className={`map-bubble-toggle${showBubbleKwh ? ' active' : ''}`}
            onClick={() => setShowBubbleKwh(v => !v)}
          >kW</span>
        </div>
      </div>

      {/* Controles Radio de búsqueda y Zoom cargadores encima del mapa */}
      <div className="map-below-controls" style={{ flexDirection: 'row', gap: '16px', alignItems: 'center', marginBottom: 4 }}>
        {!chargerQueryMode && maxDeviationValue != null && (
          <label className="map-option-inline">
            Radio de búsqueda (km)
            <input type="text" inputMode="numeric" value={localRadius} onChange={(e) => setLocalRadius(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commitRadius} onKeyDown={(e) => { if (e.key === 'Enter') commitRadius(); }} className="map-option-input" />
          </label>
        )}
        <label className="map-option-inline">
          Zoom cargadores
          <input type="text" inputMode="numeric" value={localClusterZoom} onChange={(e) => setLocalClusterZoom(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commitClusterZoom} onKeyDown={(e) => { if (e.key === 'Enter') commitClusterZoom(); }} className="map-option-input" style={{ width: 36 }} />
        </label>
      </div>

    <div style={{ position: 'relative', height: '560px', width: '100%' }}>



      {/* Banner cuando está activo el modo via-point */}
      {viaPointMode && (
        <div className="via-mode-banner">📍 {t('map.viaPointMode')}</div>
      )}
      {mapMode === 'setOrigin' && (
        <div className="via-mode-banner" style={{ background: '#dc2626' }}>📍 {t('map.clickSetOrigin')}</div>
      )}
      {mapMode === 'setDestination' && (
        <div className="via-mode-banner" style={{ background: '#16a34a' }}>📍 {t('map.clickSetDestination')}</div>
      )}

      <MapContainer
        center={center}
        zoom={origin && destination ? 7 : 5}
        style={{ height: '100%', width: '100%' }}
        className={(viaPointMode || mapMode) ? 'map-crosshair-mode' : undefined}
        scrollWheelZoom={true}
        doubleClickZoom={false}
        maxBounds={[[34.5, -12.0], [51.5, 10.0]]}
        maxBoundsViscosity={1.0}
        minZoom={4}
      >
        <MapRefSetter mapRef={leafletMapRef} />
        <ZoomDisplay />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {bounds && <FitBounds bounds={bounds} />}

        {/* Maneja clicks para añadir via-points o seleccionar origen/destino */}
        <MapClickHandler
          viaPointMode={viaPointMode}
          onAddViaPoint={onAddMapViaPoint || (() => {})}
          mapMode={mapMode}
          onSetOrigin={onSetOrigin}
          onSetDestination={onSetDestination}
          routePositions={routePositions}
          explorePins={explorePins}
          onAddExplorePin={handleAddExplorePin}
          onRemoveExplorePin={handleRemoveExplorePin}
          chargerQueryMode={chargerQueryMode}
        />

        {/* Tooltip de ubicación al mover ratón en modo origen/destino */}
        <MapModeTooltip mapMode={mapMode} />

        {routePositions.length > 1 && (
          <RouteDistanceTooltip
            routePositions={routePositions}
            totalDistanceKm={totalDistanceKm}
            pinnedMarks={pinnedMarks}
            routeStops={routeStops}
            onAddPin={handleAddPin}
            onRemovePin={handleRemovePin}
          />
        )}

        {routePositions.length > 0 && (
          <Polyline
            positions={routePositions}
            color={selectedAltRoute ? '#dc2626' : '#2563eb'}
            weight={selectedAltRoute ? 6 : 4}
            opacity={selectedAltRoute ? 0.95 : 0.8}
          />
        )}

        {/* Alternative route charger stops */}
        {selectedAltRoute && selectedAltRoute.stops && selectedAltRoute.stops.map((stop, idx) => {
          const c = stop.charger;
          if (!c.coordinates?.lat || !c.coordinates?.lng) return null;
          return (
            <Marker
              key={`alt-stop-${idx}`}
              position={[c.coordinates.lat, c.coordinates.lng]}
              icon={L.divIcon({
                className: 'alt-route-marker',
                html: `<div style="background:#7c3aed;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:0.75rem;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${idx + 1}</div>`,
                iconSize: [28, 28],
                iconAnchor: [14, 14],
              })}
            >
              <Popup>
                <div style={{fontSize:'0.82rem'}}>
                  <strong>Parada {idx + 1}</strong><br/>
                  {c.operator || c.name}<br/>
                  🔋 {stop.arrivalPct}% → {stop.departurePct}%<br/>
                  ⚡ {stop.kwhCharged} kWh • ⏱ {stop.chargeMinutes} min<br/>
                  {stop.cost !== null && <>💰 {stop.cost.toFixed(2)}€ ({stop.pricePerKwh?.toFixed(2)}€/kWh)</>}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Origen */}
        {origin && (
          <Marker position={[origin.lat, origin.lng]} icon={redIcon} draggable={!!onDragOrigin}
            eventHandlers={onDragOrigin ? { dragend: (e) => { const ll = e.target.getLatLng(); onDragOrigin({ lat: ll.lat, lng: ll.lng }); } } : {}}>
            <Popup>
              <strong>{t("map.origin")}</strong><br />
              {origin.name || t("map.startPoint")}
            </Popup>
          </Marker>
        )}

        {/* Destino */}
        {destination && (
          <Marker position={[destination.lat, destination.lng]} icon={greenIcon} draggable={!!onDragDestination}
            eventHandlers={onDragDestination ? { dragend: (e) => { const ll = e.target.getLatLng(); onDragDestination({ lat: ll.lat, lng: ll.lng }); } } : {}}>
            <Popup>
              <strong>{t("map.destination")}</strong><br />
              {destination.name || t("map.endPoint")}
            </Popup>
          </Marker>
        )}

        {/* Preview markers (before route calculation) */}
        {!origin && previewOrigin && (
          <Marker position={[previewOrigin.lat, previewOrigin.lng]} icon={previewOriginIcon}>
            <Popup>
              <strong>📍 {t("map.origin")}</strong><br />
              {previewOrigin.name || t("map.startPoint")}
            </Popup>
          </Marker>
        )}
        {!destination && previewDestination && (
          <Marker position={[previewDestination.lat, previewDestination.lng]} icon={previewDestIcon}>
            <Popup>
              <strong>📍 {t("map.destination")}</strong><br />
              {previewDestination.name || t("map.endPoint")}
            </Popup>
          </Marker>
        )}

        {/* Puntos de paso libres (via-points) */}
        {mapViaPoints.map((vp, idx) => (
          <Marker key={`via-${idx}-${vp.lat}-${vp.lng}`} position={[vp.lat, vp.lng]} icon={viaIcon} zIndexOffset={800}>
            <Popup>
              <div style={{ fontSize: '0.85em' }}>
                <strong>🛤️ {t('map.viaPoints')} #{idx + 1}</strong><br />
                {vp.lat.toFixed(4)}, {vp.lng.toFixed(4)}<br />
                {onRemoveMapViaPoint && (
                  <button
                    onClick={() => onRemoveMapViaPoint(idx)}
                    style={{ marginTop: 4, padding: '3px 8px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}
                  >
                    ✕ {t('map.removeViaPoint')}
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Marcas fijadas en la ruta (dblclick) */}
        {pinnedMarks.map((pin, idx) => {
          const totalKm = totalDistanceKm || pin.totalKm || 0;
          const { prevKm, nextKm } = findPrevNextKm(pin.km, allMarkedPoints.filter(p => Math.abs(p.km - pin.km) > 0.1), totalKm);
          const pinKey = `${pin.lat.toFixed(5)}-${pin.lng.toFixed(5)}`;
          const isLoadingChargers = loadingPinChargers[pinKey];
          const pinChargersCount = pinChargers[pinKey]?.length || 0;

          return (
            <Marker key={`pin-${idx}-${pin.km}`} position={[pin.lat, pin.lng]} icon={pinIcon} zIndexOffset={700}
              eventHandlers={{ dblclick: () => handleRemovePin(idx) }}>
              <Popup autoClose={false} closeOnClick={false}>
                <div style={{ fontSize: '0.85em', lineHeight: 1.5 }}>
                  <strong>📍 {pin.km} km</strong><br />
                  <span style={{ color: '#1e40af', fontWeight: 600 }}>
                    {pin.km.toFixed(0)}km – {(totalKm - pin.km).toFixed(0)}km
                  </span><br />
                  <span style={{ fontSize: '0.88em', color: '#555' }}>
                    * {(pin.km - prevKm).toFixed(0)}km * {(nextKm - pin.km).toFixed(0)}km *
                  </span><br />
                  <span style={{ color: '#666', fontSize: '0.88em' }}>
                    {isLoadingChargers ? t("chargers.loading") : `⚡ ${pinChargersCount} ${t("map.nearbyChargers")}`}
                  </span>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Paradas de carga asignadas */}
        {routeStops.map((stop, idx) => (
          <Marker key={`stop-${idx}-${stop.charger?.id}`} position={[stop.lat, stop.lng]} icon={stopIcon} zIndexOffset={900}>
            <Popup>
              <div style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                <strong>⚡ {t("map.chargingStop")} #{idx + 1}</strong><br />
                {stop.charger?.operator || t("chargers.unknown")}<br />
                <span style={{ color: '#2563eb' }}>{stop.km?.toFixed(1)} km {t("map.fromOrigin")}</span>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Puntos km de la pestaña Paradas (marcados como pins azules) */}
        {tabKmPins.map((pin, idx) => (
          <Marker key={`tabkm-${idx}-${pin.km}`} position={[pin.lat, pin.lng]} icon={pinIcon} zIndexOffset={700}
            eventHandlers={{ dblclick: () => onRemoveTabKmPoint && onRemoveTabKmPoint(pin.km) }}>
            <Popup>
              <div style={{ fontSize: '0.85em', lineHeight: 1.5 }}>
                <strong>📍 {pin.km} km</strong> <span style={{ color: '#666', fontSize: '0.85em' }}>({t('tab.stops')})</span>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Puntos de exploración (doble clic fuera de ruta) */}
        {explorePins.map((pin, idx) => {
          const pinKey = `${pin.lat.toFixed(5)}-${pin.lng.toFixed(5)}`;
          const isLoading = loadingExplorePinChargers[pinKey];
          const chargerCount = explorePinChargers[pinKey]?.length || 0;
          return (
            <Marker key={`explore-${idx}-${pin.lat}-${pin.lng}`} position={[pin.lat, pin.lng]} icon={exploreIcon} zIndexOffset={650}>
              <Popup autoClose={false} closeOnClick={false}>
                <div style={{ fontSize: '0.85em', lineHeight: 1.5 }}>
                  <strong>🔍 {t('map.explorePoint')}</strong><br />
                  <span style={{ color: '#0d9488', fontSize: '0.88em' }}>
                    {pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}
                  </span><br />
                  <span style={{ color: '#666', fontSize: '0.88em' }}>
                    {isLoading ? t("chargers.loading") : `⚡ ${chargerCount} ${t("map.nearbyChargers")}`}
                  </span><br />
                  <span style={{ color: '#999', fontSize: '0.78em' }}>{t('map.dblclickToRemove')}</span>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Marcadores de cargadores agrupados (clustered) */}
        <ChargerClusterGroup
          visibleChargers={visibleChargers}
          ranges={ranges}
          showBubblePrice={showBubblePrice}
          showBubbleKwh={showBubbleKwh}
          openPopupChargerIds={openPopupChargerIds}
          setOpenPopupChargerIds={setOpenPopupChargerIds}
          openPopupChargerIdsRef={openPopupChargerIdsRef}
          routePositions={routePositions}
          cumDists={cumDists}
          totalDistanceKm={totalDistanceKm}
          allMarkedPoints={allMarkedPoints}
          routeStops={routeStops}
          favoriteChargers={favoriteChargers}
          onToggleFavorite={onToggleFavorite}
          onAddRouteStop={onAddRouteStop}
          getIcon={getChargerIconForConfig}
          groupConnectorsFn={groupConnectors}
          clusterZoom={clusterZoom}
        />
      </MapContainer>

      {/* Loading overlay (appears after 1 second of loading) */}
      {showLoadingOverlay && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1200, background: 'rgba(255,255,255,0.95)', padding: '10px 24px', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', fontSize: '0.92rem', fontWeight: 600, color: '#334155', border: '1px solid #e2e8f0', pointerEvents: 'none' }}>
          {t('chargers.loading')}
        </div>
      )}

      {/* Leyenda de potencias (interactiva) */}
      <PowerLegend t={t} powerRanges={ranges} legendFilters={legendFilters} onToggleLegendFilter={handleToggleLegendFilter}
        powerRangeConfig={powerRangeConfig} updatePowerRange={updatePowerRange} savePowerRangeConfig={savePowerRangeConfig}
        resetPowerRangeConfig={resetPowerRangeConfig} configSaved={configSaved} currentUser={currentUser} mapRef={leafletMapRef} />

      {/* Leyenda de operadores (interactiva) — encima de conectores */}
      <OperatorLegend
        t={t}
        operators={chargerQueryMode ? allDbOperators : (operatorList.length > 0 ? operatorList : DEFAULT_FAVORITE_OPERATORS_DISPLAY)}
        operatorFilters={chargerQueryMode ? queryOperatorFilters : operatorFilters}
        onToggleOperatorFilter={chargerQueryMode ? handleToggleQueryOperator : handleToggleOperatorFilter}
        defaultCollapsed={!chargerQueryMode}
        mapRef={leafletMapRef}
      />

      {/* Leyenda de tipos de conectores (interactiva) */}
      <ConnectorTypeLegend t={t} connectorTypes={connectorTypes} connectorFilters={connectorFilters} onToggleConnectorFilter={handleToggleConnectorFilter} mapRef={leafletMapRef} />

      {/* Info: clic sobre la ruta / mapa */}
      {showInfoTip && (routePositions.length > 1 ? (
        <div ref={infoTipDrag.dragRef} className="map-route-info-tip" style={{ position: 'absolute', bottom: 8, left: '50%', transform: infoTipDrag.dragStyle.left != null ? 'none' : 'translateX(-50%)', zIndex: 1000, background: 'rgba(241,245,249,0.95)', color: '#334155', padding: '4px 14px', borderRadius: '16px', fontSize: '0.78em', fontWeight: 500, pointerEvents: 'auto', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', cursor: infoTipDrag.dragging ? 'grabbing' : 'grab', userSelect: 'none', ...infoTipDrag.dragStyle }} onMouseDown={infoTipDrag.onDragStart} onTouchStart={infoTipDrag.onDragStart}>
          ℹ️ {t('map.routeClickInfo')} <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#2563eb', verticalAlign: 'middle', margin: '0 3px', boxShadow: '0 0 3px rgba(37,99,235,0.5)' }}></span> {t('map.routeClickResult')} · {t('map.dblclickExplore')} <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#10b981', verticalAlign: 'middle', margin: '0 3px', boxShadow: '0 0 3px rgba(16,185,129,0.5)' }}></span> {t('map.dblclickExploreResult')}
          <span className="info-tip-close" onClick={() => setShowInfoTip(false)} style={{ marginLeft: 8, cursor: 'pointer', color: '#6b7280', fontWeight: 700, fontSize: '1.1em' }} title="✕">✕</span>
        </div>
      ) : (
        <div ref={infoTipDrag.dragRef} className="map-route-info-tip" style={{ position: 'absolute', bottom: 8, left: '50%', transform: infoTipDrag.dragStyle.left != null ? 'none' : 'translateX(-50%)', zIndex: 1000, background: 'rgba(241,245,249,0.95)', color: '#334155', padding: '4px 14px', borderRadius: '16px', fontSize: '0.78em', fontWeight: 500, pointerEvents: 'auto', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', cursor: infoTipDrag.dragging ? 'grabbing' : 'grab', userSelect: 'none', ...infoTipDrag.dragStyle }} onMouseDown={infoTipDrag.onDragStart} onTouchStart={infoTipDrag.onDragStart}>
          🔍 {t('map.dblclickExploreNoRoute')}
          <span className="info-tip-close" onClick={() => setShowInfoTip(false)} style={{ marginLeft: 8, cursor: 'pointer', color: '#6b7280', fontWeight: 700, fontSize: '1.1em' }} title="✕">✕</span>
        </div>
      ))}
    </div>

    </div>
  );
}
